import { useEffect, useState } from "react";
import { MapPin, CalendarDays } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { Kente, LogoBadge } from "./kit";

const calc = (target) => {
  const diff = Math.max(0, new Date(target).getTime() - Date.now());
  return {
    done: diff === 0,
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

export const Countdown = ({ target }) => {
  const [t, setT] = useState(() => calc(target));
  useEffect(() => {
    const id = setInterval(() => setT(calc(target)), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (t.done)
    return <p data-testid="countdown-live" className="font-display text-2xl text-amber-400">The festival is here — àṣẹ!</p>;
  const cells = [["days", "Days"], ["hours", "Hrs"], ["minutes", "Min"], ["seconds", "Sec"]];
  return (
    <div className="grid max-w-md grid-cols-4 gap-2" data-testid="countdown">
      {cells.map(([k, label]) => (
        <div key={k} className="rounded-md border border-amber-500/30 bg-neutral-950/70 px-2 py-3 text-center backdrop-blur-sm">
          <div data-testid={`countdown-${k}`} className="font-display text-3xl font-bold tabular-nums text-amber-400 sm:text-4xl">
            {String(t[k]).padStart(2, "0")}
          </div>
          <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-400">{label}</div>
        </div>
      ))}
    </div>
  );
};

export const Hero = ({ content }) => {
  const hero = content.images?.hero;
  return (
    <section id="top" className="grain relative flex min-h-[92vh] flex-col justify-end overflow-hidden" data-testid="hero-section">
      {hero ? (
        <img src={imgUrl(hero.url)} alt={hero.caption || "Egúngún masquerades at sunset"} className="absolute inset-0 h-full w-full object-cover object-center" data-testid="hero-image" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-red-950 to-neutral-950" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/70 to-neutral-950/10" />
      <div className="relative z-10 mx-auto w-full max-w-6xl px-5 pb-14 pt-24">
        <LogoBadge content={content} className="rise mb-6 h-24 w-36 rounded-2xl p-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.6)] sm:h-28 sm:w-44" testid="hero-logo" />
        <br />
        <p className="rise mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-600/50 bg-neutral-950/70 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-emerald-400 backdrop-blur-sm">
          <MapPin className="h-3.5 w-3.5" /> {content.event_subtitle} · Gainesville, FL
        </p>
        <h1 className="rise font-display text-6xl font-bold leading-[0.85] text-gold drop-shadow-[0_4px_20px_rgba(0,0,0,0.7)] sm:text-7xl lg:text-8xl" style={{ animationDelay: "80ms" }} data-testid="hero-title">
          {content.event_title}
          <span className="mt-1 block">
            <span className="sway font-script text-5xl normal-case tracking-normal text-red-500 sm:text-6xl lg:text-7xl">Festival</span>
            <span className="ml-3 text-white">{content.year}</span>
          </span>
        </h1>
        <p className="rise mt-6 max-w-xl text-lg font-semibold leading-snug text-neutral-100 sm:text-xl" style={{ animationDelay: "160ms" }} data-testid="hero-tagline">
          {content.tagline}
        </p>
        <p className="rise mt-3 flex items-center gap-2 font-display text-lg text-amber-300" style={{ animationDelay: "220ms" }} data-testid="hero-dates">
          <CalendarDays className="h-5 w-5" /> {content.dates_label}
        </p>
        <div className="rise mt-7" style={{ animationDelay: "280ms" }}>
          <Countdown target={content.countdown_target} />
        </div>
        <div className="rise mt-8 flex flex-col gap-3 sm:flex-row" style={{ animationDelay: "340ms" }}>
          <a href="#register" data-testid="hero-register-cta" className="rounded-full bg-amber-500 px-8 py-4 text-center font-display text-lg font-semibold text-neutral-950 shadow-[0_0_24px_rgba(245,158,11,0.35)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-amber-400">
            Register Your Household
          </a>
          <a href="#support" data-testid="hero-donate-cta" className="rounded-full border border-neutral-600 bg-neutral-950/60 px-8 py-4 text-center font-display text-lg text-neutral-100 backdrop-blur-sm transition-colors hover:border-amber-500 hover:text-amber-400">
            Support the Festival
          </a>
        </div>
      </div>
      <Kente />
    </section>
  );
};
